import React from "react";
import { Box, Button, CircularProgress, Fade, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { PAGE_TEXT, STATUS_ACTION, STATUS_TONE } from "./paymentPage.constants";

const FADE_DURATION_MILLISECONDS = 800;
const STATUS_ICON_SIZE = 80;
const SCALE_IN_KEYFRAMES = `
    @keyframes scaleIn {
        from {
            transform: scale(0);
            opacity: 0;
        }
        to {
            transform: scale(1);
            opacity: 1;
        }
    }
`;

const STATUS_PANEL_STYLE = Object.freeze({
    textAlign: "center",
    py: 5,
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
});

const ACTION_BUTTON_STYLE = Object.freeze({ mt: 3, mx: "auto" });

function SuccessIcon() {
    return (
        <>
            <CheckCircleOutlineIcon
                sx={{
                    fontSize: STATUS_ICON_SIZE,
                    color: "success.main",
                    mb: 2,
                    mx: "auto",
                    animation: "scaleIn 0.5s ease-out",
                }}
            />
            <style>{SCALE_IN_KEYFRAMES}</style>
        </>
    );
}

function WarningIcon() {
    return <WarningAmberIcon sx={{ fontSize: STATUS_ICON_SIZE, color: "warning.main", mb: 2, mx: "auto" }} />;
}

function ErrorIcon() {
    return <ErrorOutlineIcon sx={{ fontSize: STATUS_ICON_SIZE, color: "error.main", mb: 2, mx: "auto" }} />;
}

function ProgressIcon() {
    return (
        <Box sx={{ mb: 2, mx: "auto" }}>
            <CircularProgress />
        </Box>
    );
}

const STATUS_ICON_BY_TONE = Object.freeze({
    [STATUS_TONE.SUCCESS]: SuccessIcon,
    [STATUS_TONE.WARNING]: WarningIcon,
    [STATUS_TONE.ERROR]: ErrorIcon,
    [STATUS_TONE.PROGRESS]: ProgressIcon,
});

const HEADING_COLOUR_BY_TONE = Object.freeze({
    [STATUS_TONE.SUCCESS]: "success.main",
    [STATUS_TONE.WARNING]: "warning.main",
    [STATUS_TONE.ERROR]: "error.main",
    [STATUS_TONE.PROGRESS]: "text.primary",
});

function NoStatusAction() {
    return <></>;
}

function TryAgainAction({ onTryAgain }) {
    return (
        <Button variant="contained" sx={ACTION_BUTTON_STYLE} onClick={onTryAgain}>
            {PAGE_TEXT.TRY_AGAIN}
        </Button>
    );
}

function ReloadAction({ onReload }) {
    return (
        <Button variant="contained" sx={ACTION_BUTTON_STYLE} onClick={onReload}>
            {PAGE_TEXT.RELOAD_PAGE}
        </Button>
    );
}

const STATUS_ACTION_BY_KIND = Object.freeze({
    [STATUS_ACTION.NONE]: NoStatusAction,
    [STATUS_ACTION.TRY_AGAIN]: TryAgainAction,
    [STATUS_ACTION.RELOAD]: ReloadAction,
});

function PaymentPageStatus({ pageView, onTryAgain, onReload }) {
    const StatusIcon = STATUS_ICON_BY_TONE[pageView.tone];
    const StatusAction = STATUS_ACTION_BY_KIND[pageView.action];
    return (
        <Fade in timeout={FADE_DURATION_MILLISECONDS}>
            <Box sx={STATUS_PANEL_STYLE}>
                <StatusIcon />
                <Typography variant="h4" color={HEADING_COLOUR_BY_TONE[pageView.tone]} sx={{ mb: 1, fontWeight: 600 }}>
                    {pageView.heading}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    {pageView.body}
                </Typography>
                <StatusAction onTryAgain={onTryAgain} onReload={onReload} />
            </Box>
        </Fade>
    );
}

export default PaymentPageStatus;
